import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface RetirementFormProps {
  isOpen: boolean;
  onClose: () => void;
}

const RetirementForm: React.FC<RetirementFormProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    yourName: '',
    spouseName: '',
    yourAge: '',
    spouseAge: '',
    email: '',
    annualSocialSecurity: '',
    annualPensions: '',
    totalRetirementAssets: '',
    desiredRetirementIncome: '',
    desiredRetirementAge: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log('Submitting form data:', formData);
      
      const response = await fetch(
        'https://ggavtngjpeppxzdahgwx.supabase.co/functions/v1/9901e377-36ec-4411-8baf-f77f09a387d5',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        }
      );

      const result = await response.json();
      console.log('Function response:', result);
      
      if (response.ok && result.success) {
        toast({
          title: "Success!",
          description: "Your retirement analysis request has been sent. We'll contact you soon!",
        });
        
        setFormData({
          yourName: '',
          spouseName: '',
          yourAge: '',
          spouseAge: '',
          email: '',
          annualSocialSecurity: '',
          annualPensions: '',
          totalRetirementAssets: '',
          desiredRetirementIncome: '',
          desiredRetirementAge: '',
          comments: ''
        });
        onClose();
      } else {
        throw new Error(result.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast({
        title: "Error",
        description: "Failed to send your request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-slate-700">
            Get Your Personalized Retirement Analysis
          </DialogTitle>
          <DialogDescription className="text-center text-slate-600">
            Dan Casey will create the report and email it to you with a personalized video explainer
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center space-x-4">
                <img
                  src="https://d64gsuwffb70l.cloudfront.net/68529ada89b91746d63b58af_1750648410025_ad332db3.jpeg"
                  alt="Dan Casey"
                  className="w-16 h-16 rounded-full object-cover border-2 border-white"
                />
                <div>
                  <h3 className="text-xl font-bold">Dan Casey</h3>
                  <p className="text-slate-100">Independent Registered Investment Representative</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <Users className="w-3 h-3" />
                    <span className="text-xs">25+ Years Experience</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="yourName" className="text-sm text-slate-700">Your Name</Label>
                <Input
                  id="yourName"
                  name="yourName"
                  value={formData.yourName}
                  onChange={handleInputChange}
                  className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="spouseName" className="text-sm text-slate-700">Spouse Name</Label>
                <Input
                  id="spouseName"
                  name="spouseName"
                  value={formData.spouseName}
                  onChange={handleInputChange}
                  className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="yourAge" className="text-sm text-slate-700">Your Age</Label>
                <Input
                  id="yourAge"
                  name="yourAge"
                  type="number"
                  value={formData.yourAge}
                  onChange={handleInputChange}
                  className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="spouseAge" className="text-sm text-slate-700">Spouse Age</Label>
                <Input
                  id="spouseAge"
                  name="spouseAge"
                  type="number"
                  value={formData.spouseAge}
                  onChange={handleInputChange}
                  className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-sm text-slate-700">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
                required
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="desiredRetirementAge" className="text-sm text-slate-700">Desired Retirement Age (enter 0 if already retired)</Label>
              <Input
                id="desiredRetirementAge"
                name="desiredRetirementAge"
                type="number"
                placeholder="65"
                value={formData.desiredRetirementAge}
                onChange={handleInputChange}
                className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="annualSocialSecurity" className="text-sm text-slate-700">Annual Household Social Security</Label>
              <Input
                id="annualSocialSecurity"
                name="annualSocialSecurity"
                type="number"
                placeholder="$0"
                value={formData.annualSocialSecurity}
                onChange={handleInputChange}
                className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="annualPensions" className="text-sm text-slate-700">Annual Household Pensions</Label>
              <Input
                id="annualPensions"
                name="annualPensions"
                type="number"
                placeholder="$0"
                value={formData.annualPensions}
                onChange={handleInputChange}
                className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="totalRetirementAssets" className="text-sm text-slate-700">Total Retirement Assets Not Including Real Estate</Label>
              <Input
                id="totalRetirementAssets"
                name="totalRetirementAssets"
                type="number"
                placeholder="$0"
                value={formData.totalRetirementAssets}
                onChange={handleInputChange}
                className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="desiredRetirementIncome" className="text-sm text-slate-700">Desired Retirement Income</Label>
              <Input
                id="desiredRetirementIncome"
                name="desiredRetirementIncome"
                type="number"
                placeholder="$0"
                value={formData.desiredRetirementIncome}
                onChange={handleInputChange}
                className="h-9 border-slate-300 focus:border-slate-600 focus:ring-slate-600"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="comments" className="text-sm text-slate-700">Comments</Label>
              <Textarea
                id="comments"
                name="comments"
                placeholder="Any additional information or questions you'd like to share..."
                value={formData.comments}
                onChange={handleInputChange}
                className="min-h-[80px] border-slate-300 focus:border-slate-600 focus:ring-slate-600"
              />
            </div>

            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-slate-600 to-slate-700 hover:from-slate-700 hover:to-slate-800 text-white font-semibold py-2 mt-6"
            >
              {isSubmitting ? 'Sending...' : 'Get My Personalized Analysis'}
            </Button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RetirementForm;